import { get } from 'svelte/store';

import type { SettingsConfig } from 'src/types/Settings';

import { user_data } from 'src/stores/account/user';
import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';

// Legacy components — Home settings (per FigJam Multi-home settings board)
import GetKey from 'src/components/Settings/AccountSettings/Security/GetKey.svelte';
import GrantInstallerAccess from 'src/components/Settings/AccountSettings/GrantInstallerAccess.svelte';
import AppBackground from 'src/components/Settings/AppSettings/AppBackground.svelte';
import HomeInfo from 'src/components/Settings/HomeManagement/HomeInfo.svelte';
import LockPin from 'src/components/Settings/AppSettings/LockPin.svelte';
import MenuItemsOrder from 'src/components/Settings/AppSettings/MenuItemsOrder.svelte';
import SelectImage from 'src/components/Settings/Tools/SelectImage.svelte';
import StatisticsSettings from 'src/components/Settings/AppSettings/StatisticsSettings.svelte';
import TimeZone from 'src/components/Settings/AccountSettings/Localization/TimeZone.svelte';
import MyHomes from 'src/components/Settings/HomeManagement/MyHomes.svelte';
import ShareHomeAccess from 'src/components/Settings/HomeManagement/ShareHomeAccess.svelte';
import EnergyMeterSetup from 'src/components/Settings/HomeManagement/EnergyMeterSetup.svelte';

// V2 components — Home settings (new layout)
import GetKeyV2 from 'src/components/Settings/AccountSettings/Security/GetKeyV2.svelte';
import GrantInstallerAccessV2 from 'src/components/Settings/AccountSettings/GrantInstallerAccessV2.svelte';
import TimeZoneV2 from 'src/components/Settings/AccountSettings/Localization/TimeZoneV2.svelte';
import GetToKnowHomeManagement from 'src/components/Settings/HomeManagement/GetToKnowHomeManagement.svelte';
import MyHomesV2 from 'src/components/Settings/HomeManagement/MyHomesV2.svelte';
import ShareHomeAccessV2 from 'src/components/Settings/HomeManagement/ShareHomeAccessV2.svelte';
import EnergyMeterSetupV2 from 'src/components/Settings/HomeManagement/EnergyMeterSetupV2.svelte';
import { isSubAccount } from 'src/helpers/user_helpers';
import EnergyPricesSettingsV2 from 'src/components/Settings/HomeManagement/EnergyPricesSettingsV2.svelte';
import EnergyPriceLocationV2 from 'src/components/Settings/AccountSettings/Localization/EnergyPriceLocationV2.svelte';
import MonthlyReportsV2 from 'src/components/Settings/HomeManagement/MonthlyReportsV2.svelte';
import PvSetupSettingsGuarded from 'src/components/Settings/HomeManagement/PvSetupSettingsGuarded.svelte';
import { isEnergyPriceFeatureEnabled } from 'src/helpers/energy_price_helpers';

const isInstaller = (): boolean => Boolean(get(user_data)?.installer);
const isOwnedHomeActive = (): boolean => !isActiveHomeShared();

export const homeSettingsConfig: SettingsConfig = {
	legacy: [
		{
			id: 'homeInfo',
			title: 'label_home_info',
			titleDefault: 'Home info',
			component: HomeInfo,
			lineContainer: true,
		},
		{
			id: 'myHomes',
			title: 'settings_my_homes',
			titleDefault: 'My Homes',
			subtitle: 'settings_my_homes_description',
			subtitleDefault: 'View and manage your homes.',
			component: MyHomes,
			visible: () => !isInstaller(),
		},
		{
			id: 'shareHomeAccess',
			title: 'settings_share_home_access',
			titleDefault: 'Share home access',
			subtitle: 'settings_share_home_access_description',
			subtitleDefault: 'Invite people to access this home.',
			component: ShareHomeAccess,
			visible: () => !isInstaller(),
		},
		{
			id: 'energyMeter',
			title: 'title_add_energy_meter_to_home',
			titleDefault: 'Add Main Energy Meter to home',
			subtitle: 'description_add_energy_meter_to_home',
			subtitleDefault:
				'Connect a main energy meter to this home to measure its total consumption.',
			component: EnergyMeterSetup,
		},
		{
			id: 'appBackground',
			title: 'title_change_app_background',
			titleDefault: 'Change App Background',
			component: AppBackground,
		},
		{
			id: 'location',
			title: 'label_location',
			titleDefault: 'Location',
			groups: [
				{
					id: 'timeZone',
					title: 'collapse_title_time_zone',
					titleDefault: 'Time zone',
					component: TimeZone,
				},
			],
		},
		{
			id: 'privacySecurity',
			title: 'label_privacy_and_security',
			titleDefault: 'Privacy and Security',
			groups: [
				{
					id: 'lockPin',
					title: 'lock_pin',
					titleDefault: 'Lock pin',
					component: LockPin,
				},
			],
		},
		{
			id: 'customizations',
			title: 'label_customizations',
			titleDefault: 'Customizations',
			visible: isOwnedHomeActive,
			groups: [
				{
					id: 'managePictures',
					title: 'title_menu_items_manage_pictures',
					titleDefault: 'Manage Pictures',
					component: SelectImage,
				},
			],
		},
		{
			id: 'accessPermissions',
			title: 'label_title_access_permissions',
			titleDefault: 'Access and permissions',
			visible: () => !isInstaller() && !isSubAccount(),
			groups: [
				{
					id: 'installerAccess',
					title: 'collapse_title_grant_installer_access',
					titleDefault: 'Grant installer access',
					component: GrantInstallerAccess,
				},
				{
					id: 'cloudKey',
					title: 'cloud_key',
					titleDefault: 'Cloud Key',
					component: GetKey,
				},
			],
		},
		{
			id: 'personalization',
			title: 'label_personalization',
			titleDefault: 'Personalization',
			visible: isOwnedHomeActive,
			groups: [
				{
					id: 'menuOrder',
					title: 'collapse_title_menu_order',
					titleDefault: 'Menu order',
					component: MenuItemsOrder,
				},
				{
					id: 'statistics',
					title: 'collapse_title_statistics',
					titleDefault: 'Statistics',
					component: StatisticsSettings,
				},
			],
		},
	],
	v2: [
		{
			id: 'homeInfo',
			title: 'label_home_info',
			titleDefault: 'Home info',
			component: HomeInfo,
			lineContainer: true,
		},
		{
			id: 'myHomes',
			title: 'settings_my_homes',
			titleDefault: 'My Homes',
			subtitle: 'settings_my_homes_description',
			subtitleDefault: 'View and manage your homes.',
			visible: () => !isInstaller(),
			sectionClass: 'pb-m',
			groups: [
				{
					id: 'getToKnowHomeManagement',
					title: 'homes_onboarding_alert_heading',
					titleDefault: 'Get to know home management',
					subtitle: 'homes_onboarding_alert_message',
					subtitleDefault:
						'Learn how to manage multiple homes, transfer ownership, receive homes, and share access with family or guests.',
					visible: () => !isSubAccount(),
					components: [
						{
							id: 'getToKnowHomeManagement',
							component: GetToKnowHomeManagement,
						},
					],
				},
				{
					id: 'homesGroup',
					title: 'settings_homes_group',
					titleDefault: 'Homes',
					subtitle: 'settings_homes_group_description',
					subtitleDefault: 'Homes owned by you or shared.',
					components: [{ id: 'myHomes', component: MyHomesV2 }],
				},
				{
					id: 'shareHomeAccessGroup',
					components: [{ id: 'shareHomeAccess', component: ShareHomeAccessV2 }],
				},
			],
		},
		{
			id: 'location',
			title: 'label_location',
			titleDefault: 'Location',
			subtitle: 'settings_location_subtitle_new',
			subtitleDefault: 'Manage Location settings of your home',
			groups: [
				{
					id: 'timeZoneGroup',
					components: [{ id: 'timeZone', component: TimeZoneV2 }],
				},
			],
		},
		{
			id: 'accessPermissions',
			title: 'label_title_access_permissions',
			titleDefault: 'Access and permissions',
			subtitle: 'settings_access_permissions_subtitle_new',
			subtitleDefault: 'Control who can access your home and devices.',
			visible: () => !isInstaller() && !isSubAccount(),
			groups: [
				{
					id: 'installerAccessGroup',
					title: 'collapse_title_grant_installer_access',
					titleDefault: 'Grant installer access',
					components: [
						{
							id: 'installerAccess',
							component: GrantInstallerAccessV2,
						},
					],
				},
				{
					id: 'cloudKeyGroup',
					title: 'cloud_key',
					titleDefault: 'Cloud key',
					components: [{ id: 'authKey', component: GetKeyV2 }],
				},
			],
		},
		{
			id: 'energy',
			title: 'label_energy',
			titleDefault: 'Energy',
			subtitle: 'settings_energy_subtitle',
			subtitleDefault:
				'Manage electricity tariffs, energy meters, solar setup, and monthly reports for this home.',
			visible: () => !isInstaller(),
			groups: [
				{
					id: 'energyPricesGroup',
					components: [
						{
							id: 'energyPrices',
							component: EnergyPricesSettingsV2,
						},
						{
							id: 'energyPriceLocation',
							component: EnergyPriceLocationV2,
							visible: () => isEnergyPriceFeatureEnabled(),
						},
						{ id: 'monthlyReports', component: MonthlyReportsV2 },
						{ id: 'energyMeter', component: EnergyMeterSetupV2 },
						{ id: 'pvSetup', component: PvSetupSettingsGuarded },
					],
				},
			],
		},
	],
};
